import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Search, ArrowRight, FileText } from 'lucide-react';
import FFLoader from '../components/FFLoader';
import JsonLd from '../components/JsonLd';

const API = process.env.REACT_APP_BACKEND_URL;

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async (search = '') => {
    try {
      let url = `${API}/api/templates/pseo`;
      if (search) url += `?search=${encodeURIComponent(search)}`;
      const res = await fetch(url);
      const data = await res.json();
      setTemplates(data);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
      document.title = '100% Free Online Form Building Templates / Fantastic Forms';
      const canonical = document.getElementById('canonical-link') || document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.href = 'https://fantasticforms.com/templates';
      }
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchTemplates(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <FFLoader size={48} />
      </div>
    );
  }

  // JSON-LD: gallery is a CollectionPage with an ItemList of templates.
  // We omit ItemList entries when filtering client-side so the schema
  // stays canonical (matches what an unauthenticated crawler sees).
  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Free Online Form Templates',
    description: 'Free, customizable online form templates. Surveys, registrations, feedback, contact forms, and more.',
    url: 'https://fantasticforms.com/templates',
    isPartOf: { '@type': 'WebSite', name: 'Fantastic Forms', url: 'https://fantasticforms.com' },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: templates.length,
      itemListElement: templates.slice(0, 100).map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://fantasticforms.com/templates/${t.slug}`,
        name: t.title,
      })),
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',      item: 'https://fantasticforms.com/' },
      { '@type': 'ListItem', position: 2, name: 'Templates', item: 'https://fantasticforms.com/templates' },
    ],
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <JsonLd id="templates-collection" data={collectionLd} />
      <JsonLd id="templates-bc" data={breadcrumbLd} />
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex justify-center mb-8">
            <Link to="/" className="flex items-center gap-2" data-testid="templates-logo-link">
              <img src="/logo.png" alt="Fantastic Forms" className="w-8 h-8" />
              <span className="font-bold text-lg tracking-tight text-zinc-900">Fantastic Forms</span>
            </Link>
          </div>
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-3" data-testid="templates-heading">
              Form Templates
            </h1>
            <p className="text-zinc-500 mb-6">
              Start with a template, customize it to your needs.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
                data-testid="templates-search"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {templates.length > 0 ? (
          <div className="space-y-1" data-testid="templates-list">
            {templates.map((t) => (
              <Link
                key={t.slug}
                to={`/templates/${t.slug}`}
                className="flex items-center justify-between px-4 py-3.5 rounded-lg hover:bg-white hover:shadow-sm transition-all group"
                data-testid={`template-${t.slug}`}
              >
                <span className="text-zinc-800 group-hover:text-zinc-900 font-medium text-sm">
                  {t.title}
                </span>
                <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-600 transition-colors" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
            <h3 className="text-base font-medium text-zinc-900 mb-1">No templates found</h3>
            <p className="text-sm text-zinc-500">Try a different search term</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Templates;
